import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import API_BASE, { withApiCredentials } from "../api";
import "../styles/components/Inbox.css";
import { getLegacyAppToken } from "../auth/bootstrapTokens";
import { trackProductEvent } from "../analytics/trackEvent";
import {
  getCatchSubmitRefreshEventName,
  reportBackgroundRefreshBlocked,
  shouldBlockBackgroundRefresh,
} from "../app/catchSubmitGate";

const MESSAGE_REFRESH_EVENT = "messagesUpdated";
const MESSAGE_REFRESH_INTERVAL_MS = 5000;

export default function Inbox({ currentUser = null, onOpenChat }) {
  const token = getLegacyAppToken(window);
  const { t } = useTranslation();
  const currentUserId = String(currentUser?.id || "").trim();
  const hasAuthenticatedUser = Boolean(token || currentUserId);

  const [conversations, setConversations] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [meId, setMeId] = useState(null);
  const [isMeLoading, setIsMeLoading] = useState(Boolean(hasAuthenticatedUser));
  const [isInboxLoading, setIsInboxLoading] = useState(Boolean(hasAuthenticatedUser));
  const [searchQuery, setSearchQuery] = useState("");
  const inboxRequestIdRef = useRef(0);
  const inboxInFlightRef = useRef(false);
  const queuedInboxRefreshRef = useRef(null);
  const activeInboxPromiseRef = useRef(null);
  const inboxOpenTrackedRef = useRef(false);

  useEffect(() => {
    if (!hasAuthenticatedUser || inboxOpenTrackedRef.current) {
      return;
    }

    inboxOpenTrackedRef.current = true;
    trackProductEvent("inbox_opened", {
      featureArea: "messages",
      metadata: { source: "messages_page" },
    });
  }, [hasAuthenticatedUser]);

  useEffect(() => {
    if (currentUserId) {
      setMeId(currentUserId);
      setIsMeLoading(false);
      return;
    }

    if (!hasAuthenticatedUser) {
      setMeId(null);
      setIsMeLoading(false);
      return;
    }

    const controller = new AbortController();
    setIsMeLoading(true);

    async function loadMe() {
      try {
        const res = await fetch(
          `${API_BASE}/auth/me`,
          withApiCredentials({
            signal: controller.signal,
            headers: token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : {},
          })
        );
        if (!res.ok) return;

        const me = await res.json();
        setMeId(me.id);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Failed to load me:", err);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsMeLoading(false);
        }
      }
    }

    loadMe();
    return () => controller.abort();
  }, [currentUserId, hasAuthenticatedUser, token]);

  useEffect(() => {
    async function loadUsers() {
      try {
        const res = await fetch(`${API_BASE}/auth/users`);
        const users = await res.json();
        const map = {};
        users.forEach((u) => {
          map[u.id] = u;
        });
        setUsersMap(map);
      } catch (err) {
        console.error("Failed to load users:", err);
      }
    }

    loadUsers();
  }, []);

  const loadInbox = useCallback(
    async ({ showLoader = false } = {}) => {
      if (!hasAuthenticatedUser) {
        setConversations([]);
        setIsInboxLoading(false);
        return;
      }

      const requestId = inboxRequestIdRef.current + 1;
      inboxRequestIdRef.current = requestId;

      if (showLoader) {
        setIsInboxLoading(true);
      }

      try {
        const res = await fetch(
          `${API_BASE}/messages/inbox`,
          withApiCredentials({
            headers: token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : {},
          })
        );
        if (!res.ok) return;

        const data = await res.json();
        if (requestId !== inboxRequestIdRef.current) {
          return;
        }
        setConversations(data || []);
      } catch (err) {
        console.error("Failed to load inbox:", err);
      } finally {
        if (showLoader && requestId === inboxRequestIdRef.current) {
          setIsInboxLoading(false);
        }
      }
    },
    [hasAuthenticatedUser, token]
  );

  const refreshInbox = useCallback(
    ({ showLoader = false } = {}) => {
      if (!hasAuthenticatedUser) {
        return Promise.resolve();
      }
      if (shouldBlockBackgroundRefresh()) {
        reportBackgroundRefreshBlocked({ source: "messages_inbox" });
        return Promise.resolve();
      }

      if (inboxInFlightRef.current) {
        const queuedRefresh = queuedInboxRefreshRef.current || { showLoader: false };
        queuedRefresh.showLoader = queuedRefresh.showLoader || showLoader;
        queuedInboxRefreshRef.current = queuedRefresh;
        return activeInboxPromiseRef.current || Promise.resolve();
      }

      inboxInFlightRef.current = true;
      const refreshPromise = loadInbox({ showLoader }).finally(() => {
        inboxInFlightRef.current = false;
        activeInboxPromiseRef.current = null;

        const queuedRefresh = queuedInboxRefreshRef.current;
        queuedInboxRefreshRef.current = null;
        if (queuedRefresh) {
          refreshInbox(queuedRefresh).catch(() => {});
        }
      });

      activeInboxPromiseRef.current = refreshPromise;
      return refreshPromise;
    },
    [hasAuthenticatedUser, loadInbox]
  );

  useEffect(() => {
    if (!hasAuthenticatedUser) {
      setConversations([]);
      setIsInboxLoading(false);
      return;
    }

    refreshInbox({ showLoader: true }).catch(() => {});
    return () => {
      inboxRequestIdRef.current += 1;
      inboxInFlightRef.current = false;
      queuedInboxRefreshRef.current = null;
      activeInboxPromiseRef.current = null;
    };
  }, [hasAuthenticatedUser, refreshInbox]);

  useEffect(() => {
    if (!hasAuthenticatedUser) return undefined;

    const requestInboxRefresh = () => {
      refreshInbox().catch(() => {});
    };

    const refreshVisibleInbox = () => {
      if (document.visibilityState === "visible") {
        requestInboxRefresh();
      }
    };

    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        requestInboxRefresh();
      }
    }, MESSAGE_REFRESH_INTERVAL_MS);

    const refreshEvent = getCatchSubmitRefreshEventName();
    window.addEventListener(MESSAGE_REFRESH_EVENT, requestInboxRefresh);
    window.addEventListener(refreshEvent, requestInboxRefresh);
    window.addEventListener("messagesRead", requestInboxRefresh);
    window.addEventListener("focus", requestInboxRefresh);
    document.addEventListener("visibilitychange", refreshVisibleInbox);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener(MESSAGE_REFRESH_EVENT, requestInboxRefresh);
      window.removeEventListener(refreshEvent, requestInboxRefresh);
      window.removeEventListener("messagesRead", requestInboxRefresh);
      window.removeEventListener("focus", requestInboxRefresh);
      document.removeEventListener("visibilitychange", refreshVisibleInbox);
    };
  }, [hasAuthenticatedUser, refreshInbox]);

  const showInboxLoading = isMeLoading || isInboxLoading;

  const filteredConversations = conversations.filter((c) => {
    if (!meId) return false;
    const otherId = c.fromUserId === meId ? c.toUserId : c.fromUserId;
    const otherUser = usersMap[otherId];
    const name = otherUser?.username || t("user");
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const getConversationPreview = (conversation) => {
    const previewText = (conversation?.text || "").trim();
    if (previewText) {
      return previewText;
    }

    if (conversation?.mediaUrl) {
      return conversation.mediaType === "video"
        ? t("message_preview_video", {
            defaultValue: "Sent a video",
          })
        : t("message_preview_image", {
            defaultValue: "Sent an image",
          });
    }

    const sharedPost = conversation?.sharedPost;
    if (!sharedPost) {
      return "";
    }

    const sharedCaption = (sharedPost.caption || "").trim();
    if (sharedCaption) {
      return t("shared_post_preview_caption", {
        defaultValue: "Shared post: {{caption}}",
        caption: sharedCaption,
      });
    }

    if (sharedPost.user) {
      return t("shared_post_preview_author", {
        defaultValue: "Shared post from {{user}}",
        user: sharedPost.user,
      });
    }

    return t("shared_post_preview_generic", {
      defaultValue: "Shared a post",
    });
  };

  return (
    <div className="inbox">
      <div className="inbox-header">
        <div className="inbox-title">💬 {t("inbox")}</div>

        <input
          type="text"
          className="inbox-search"
          placeholder={t("messages_search_placeholder")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="inbox-list">
        {showInboxLoading && (
          <div className="inbox-empty">
            {t("loading", { defaultValue: "Loading..." })}
          </div>
        )}

        {!showInboxLoading && filteredConversations.length === 0 && (
          <div className="inbox-empty">
            {searchQuery ? t("no_results") : t("no_conversations_yet")}
          </div>
        )}

        {!showInboxLoading &&
          filteredConversations.map((c) => {
            if (!meId) return null;

            const otherId = c.fromUserId === meId ? c.toUserId : c.fromUserId;
            const otherUser = usersMap[otherId];
            const unread = !c.read && c.toUserId === meId;

            const avatarSrc = otherUser?.avatar
              ? `${API_BASE}${otherUser.avatar}`
              : "https://cdn-icons-png.flaticon.com/512/847/847969.png";

            return (
              <div
                key={c.conversationId}
                onClick={() => onOpenChat(otherId)}
                className={`inbox-item ${unread ? "unread" : ""}`}
              >
                <img
                  src={avatarSrc}
                  alt={otherUser?.username || t("user")}
                  className="inbox-avatar"
                />

                <div className="inbox-text">
                  <div className="inbox-top">
                    <strong>{otherUser?.username || t("user")}</strong>
                    {unread && <span className="inbox-badge">{t("new_badge")}</span>}
                  </div>
                  <div className="inbox-preview">{getConversationPreview(c)}</div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
